import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabaseClient';
import type { UserProfile } from '../types/api';

type UserProfileContextValue = {
  profile: UserProfile | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
};

const UserProfileContext = createContext<UserProfileContextValue | null>(null);

const getErrorMessage = (error: unknown, fallback: string) => {
  if (error && typeof error === 'object' && 'message' in error) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === 'string') {
      return message;
    }
  }
  return fallback;
};

type UserProfileProviderProps = {
  session: Session | null;
  children: ReactNode;
};

export const UserProfileProvider = ({ session, children }: UserProfileProviderProps) => {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sessionUserId = session?.user?.id ?? null;

  const fetchProfile = useCallback(async () => {
    if (!sessionUserId) {
      setProfile(null);
      setError(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    const response = await supabase.functions.invoke<{ profile: UserProfile }>('user-roles', {
      body: { action: 'me' },
    });

    if (response.error) {
      setError(getErrorMessage(response.error, 'Failed to load profile'));
      setProfile(null);
      setLoading(false);
      return;
    }

    setProfile(response.data?.profile ?? null);
    setLoading(false);
  }, [sessionUserId]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (cancelled) return;
      await fetchProfile();
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [fetchProfile]);

  const value = useMemo<UserProfileContextValue>(
    () => ({
      profile,
      loading,
      error,
      refresh: fetchProfile,
    }),
    [fetchProfile, loading, error, profile],
  );

  return <UserProfileContext.Provider value={value}>{children}</UserProfileContext.Provider>;
};

export const useUserProfile = () => {
  const context = useContext(UserProfileContext);
  if (!context) {
    throw new Error('useUserProfile must be used within a UserProfileProvider');
  }
  return context;
};

